import React from 'react';
import { Card, CardContent, CardActionArea, Chip, Stack, Typography, Box } from '@mui/material';
import { NotificationItem } from '../types';
import { getNotificationDisplayType, normalizeNotificationType } from '../utils/notificationUtils';
import { useLanguage } from '../i18n/LocalizationContext';

interface NotificationListItemProps {
  item: NotificationItem;
  onMarkAsRead: (id: string) => Promise<void>;
  onOpenDetails: (item: NotificationItem) => void;
  loading?: boolean;
}

const NotificationListItem: React.FC<NotificationListItemProps> = ({ item, onMarkAsRead, onOpenDetails, loading = false }) => {
  const { t } = useLanguage();

  const handleClick = async () => {
    // Mark as read if not already read
    if (!item.isRead) {
      await onMarkAsRead(item.id);
    }

    // Open full message in modal.
    onOpenDetails(item);
  };

  const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (normalizeNotificationType(type)) {
      case 'leave':
        return 'primary';
      case 'regularization':
        return 'warning';
      case 'approval':
        return 'success';
      case 'bgv':
        return 'secondary';
      case 'attendance':
        return 'info';
      default:
        return 'default';
    }
  };

  const createdDate = new Date(item.createdAt);
  const timeAgo = getTimeAgo(createdDate, t);

  return (
    <Card
      sx={{
        height: '100%',
        border: `1px solid ${item.isRead ? '#e0e0e0' : '#bbdefb'}`,
        backgroundColor: item.isRead ? '#fafafa' : '#f3f7ff',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: item.isRead ? '#f5f5f5' : '#ecf3ff',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardActionArea
        onClick={handleClick}
        disabled={loading}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: '100%',
            pb: 1.5,
            '&:last-child': {
              pb: 1.5,
            },
          }}
        >
          {/* Header with Unread Indicator and Title Only */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1 }}>
              {/* Unread Indicator Dot */}
              {!item.isRead && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#2196f3',
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                />
              )}

              {/* Title Only */}
              <Typography
                variant="subtitle2"
                fontWeight={item.isRead ? 500 : 700}
                sx={{
                  color: item.isRead ? '#757575' : '#212121',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  flex: 1,
                }}
              >
                {item.title}
              </Typography>
            </Stack>
          </Stack>

          {/* Footer with Type Badge and Time */}
          <Stack
            direction="column"
            spacing={0.75}
            sx={{ width: '100%', mt: 'auto' }}
          >
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={t(getNotificationDisplayType(item.type))}
                size="small"
                color={getTypeColor(item.type)}
                variant="filled"
                sx={{
                  fontWeight: item.isRead ? 400 : 'bold',
                  opacity: item.isRead ? 0.7 : 1,
                }}
              />
              {item.isActionable && (
                <Chip
                  label={t('notifications.actionable')}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {!item.isRead && (
                <Chip
                  label={t('notifications.unreadLabel')}
                  size="small"
                  color="warning"
                  variant="filled"
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: item.isRead ? '#b0bec5' : '#1565c0',
                fontWeight: item.isRead ? 400 : 500,
              }}
            >
              {timeAgo}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/**
 * Get human-readable time difference from now
 */
function getTimeAgo(date: Date, t: (key: string, params?: Record<string, string | number>) => string): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return t('notifications.now');
  if (seconds < 3600) return t('notifications.minutesAgoShort', { count: Math.floor(seconds / 60) });
  if (seconds < 86400) return t('notifications.hoursAgoShort', { count: Math.floor(seconds / 3600) });
  if (seconds < 604800) return t('notifications.daysAgoShort', { count: Math.floor(seconds / 86400) });

  return date.toLocaleDateString();
}

export default NotificationListItem;
